<template>
  <div class="ssd-page" data-test="shapeshift-demo-page">
    <header class="ssd-head">
      <div>
        <h1 class="ssd-title">
          Tasks
          <span class="ssd-badge">ShapeShift preview</span>
        </h1>
        <p class="ssd-lede">
          One box. Type what needs to happen and the card below it changes to fit: a date for reminders,
          a person for team tasks, a patient for patient tasks. Press Enter to add.
        </p>
      </div>
      <router-link :to="{ name: 'ToDo' }" class="ssd-back">Back to Tasks</router-link>
    </header>

    <shape-shift-task-input :max-sort-id="maxSortId" @saved="onTaskSaved" />

    <p v-if="justAdded" class="ssd-added" role="status" data-test="shapeshift-demo-added">Task added.</p>

    <section class="ssd-list" aria-label="Your open tasks">
      <h2 class="ssd-list__title">
        Your open tasks
        <span class="ssd-list__count">{{ openTasks.length }}</span>
      </h2>
      <p v-if="!openTasks.length" class="ssd-empty" data-test="shapeshift-demo-empty">
        No open tasks yet. Type one above to see it land here.
      </p>
      <task-row
        v-for="task in openTasks"
        :key="task.id"
        :task="task"
        :ctx="rowCtx"
        :employees="employees"
        show-due
      />
    </section>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import ShapeShiftTaskInput from '@/components/ShapeShift/ShapeShiftTaskInput';
import TaskRow from '@/pages/TaskList/components/TaskRow';

const DEFAULT_TIME_ZONE = 'America/New_York';

export default {
  name: 'ShapeShiftDemo',

  components: { ShapeShiftTaskInput, TaskRow },

  data() {
    return {
      clock: Date.now(),
      justAdded: false,
    };
  },

  computed: {
    siteID() { return this.$store.state.customer.siteID; },
    userID() { return this.$store.state.customer.userID; },
    employees() { return this.$store.state.customer.employees || []; },

    // state.customer.settings is an ARRAY of { Property, PropertyValue } rows. Same lookup and
    // validity check as ToDo.vue::tkTimeZone.
    timeZone() {
      const rows = this.$store.state.customer.settings;
      const row = Array.isArray(rows) ? rows.find(s => s && s.Property === 'GENERAL_TIMEZONE') : null;
      const tz = row && row.PropertyValue ? String(row.PropertyValue) : '';
      return tz && moment.tz.zone(tz) ? tz : DEFAULT_TIME_ZONE;
    },

    // TaskRow's required ctx. "today" is moment's default zone, as ToDo.vue::tkCtx compares it.
    rowCtx() {
      return {
        userID: this.userID,
        today: moment(this.clock).format('YYYY-MM-DD'),
        timeZone: this.timeZone,
      };
    },

    // Tasks the user is on (assigned to them) plus ones they created for someone else, open only.
    openTasks() {
      const getters = this.$store.getters;
      const seen = new Set();
      const out = [];
      [...(getters['tasks/assignedToMeTasks'] || []), ...(getters['tasks/myCreatedTasks'] || [])].forEach(t => {
        if (!t || !t.id || seen.has(t.id)) return;
        seen.add(t.id);
        if (t.status === 'INCOMPLETE' && !t.deleted) out.push(t);
      });
      return out.sort((a, b) => (Number(b.sortID) || 0) - (Number(a.sortID) || 0));
    },

    maxSortId() {
      return this.openTasks.reduce((max, t) => Math.max(max, Number(t.sortID) || 0), 0);
    },
  },

  created() {
    // The same load ToDo.vue and the other task pages run on mount; the snapshot listener it opens
    // keeps the lists live, so a task added from the box shows up on its own.
    if (this.siteID && this.userID) {
      this.$store.dispatch('tasks/getTasksForEmployee', { siteID: this.siteID, employeeID: this.userID });
    }
    this.clockTimer = setInterval(() => { this.clock = Date.now(); }, 60000);
  },

  beforeDestroy() {
    clearInterval(this.clockTimer);
    clearTimeout(this.addedTimer);
  },

  methods: {
    onTaskSaved() {
      this.justAdded = true;
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => { this.justAdded = false; }, 3000);
    },
  },
};
</script>

<style lang="scss" scoped>
.ssd-page {
  max-width: 760px;
  margin: 0 auto;
  padding: 24px 16px 64px;
}

.ssd-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.ssd-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
  color: #1f2933;
}

.ssd-badge {
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(84, 127, 255, 0.12);
  color: #3b5bdb;
  font-size: 12px;
  font-weight: 600;
}

.ssd-lede {
  margin: 0;
  max-width: 560px;
  color: #6c757d;
  font-size: 14px;
  line-height: 1.5;
}

.ssd-back {
  flex-shrink: 0;
  font-size: 13px;
  white-space: nowrap;
}

.ssd-added {
  margin: 10px 2px 0;
  color: #1b7f4b;
  font-size: 13px;
  font-weight: 600;
}

.ssd-list {
  margin-top: 28px;
}

.ssd-list__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1f2933;
}

.ssd-list__count {
  padding: 1px 8px;
  border-radius: 999px;
  background: #f1f3f5;
  color: #495057;
  font-size: 12px;
  font-weight: 600;
}

.ssd-empty {
  margin: 0;
  padding: 20px 0;
  color: #6c757d;
  font-size: 14px;
}

@media (max-width: 575px) {
  .ssd-head {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
